import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox } from '@mui/material';
import { CustomizedSwitch, MinLoader, StatusCard, ToolInfo } from 'components';
import {
    Colors,
    evalModeEnums,
    FindLoad,
    Images,
    masteryDirectionEnums,
    masteryEvalTypeEnums,
    PermissionList,
    renderTargetStatuses,
    RolePermission,
    targetStatusEnums,
} from 'utils';
import { goalActions, libraryActions } from 'store';
import { FirstTrialSwitch, toggleFirstTrial } from '../mastery/firstTrialCriteria';
import { BASELINE_FIELDS, CriteriaRuleRow, CriteriaSaveBar, isRuleValid, MASTERY_FIELDS, useCriteriaDraft } from '../mastery/criteriaForm';

const DEFAULT_RULE = {
    direction: masteryDirectionEnums.ABOVE,
    evalModeCount: 1,
    count: 1,
    evalMode: evalModeEnums.BY_DAY,
    evalType: masteryEvalTypeEnums.CONSECUTIVE,
};

/** What differs between the per-target Teaching -> Mastered and Baseline -> Teaching cards. */
const CARDS = {
    mastery: {
        title: 'Target Mastery',
        from: targetStatusEnums.TEACHING,
        to: targetStatusEnums.MASTERED,
        storedKey: 'mastery',
        fields: MASTERY_FIELDS,
        autoKey: 'automastery',
        autoLabel: 'Auto Mastery',
        autoInfo:
            'Automatically mark a target as mastered based on the selected sessions or days — ' +
            'consecutive, or on average — and % of target mastered.',
        followLabel: 'Use goal mastery settings',
        followInfo: 'This target will use the goal’s mastery schedule',
        subject: 'Master target',
        defaults: { ...DEFAULT_RULE, automastery: true },
        actions: {
            client: {
                saveType: 'CHANGE_SINGLE_TARGET_MASTERY',
                save: goalActions.changeSingleTargetMastery,
                followType: 'SINGLE_TARGET_MASTERY_FOLLOW_GOAL',
                follow: goalActions.singleTargetMasteryFollowGoal,
            },
            library: {
                saveType: 'CHANGE_SINGLE_LIBRARY_GOAL_TARGET_MASTERY',
                save: libraryActions.changeSingleLibraryGoalTargetMastery,
                followType: 'SINGLE_LIBRARY_TARGET_MASTERY_FOLLOW_GOAL',
                follow: libraryActions.singleLibraryTargetMasteryFollowGoal,
            },
        },
    },
    baseline: {
        title: 'Baseline Criteria',
        from: targetStatusEnums.BASELINE,
        to: targetStatusEnums.TEACHING,
        storedKey: 'baselineCriteria',
        fields: BASELINE_FIELDS,
        autoKey: 'autoTransition',
        autoLabel: 'Auto Transition',
        autoInfo:
            'Automatically move this target from baseline to teaching based on the selected sessions or days — consecutive, or on average.',
        followLabel: 'Use goal baseline criteria',
        followInfo: 'This target will use the goal’s baseline criteria',
        subject: 'Move to teaching',
        defaults: { ...DEFAULT_RULE, autoTransition: false },
        actions: {
            client: {
                saveType: 'CHANGE_SINGLE_TARGET_BASELINE_CRITERIA',
                save: goalActions.changeSingleTargetBaselineCriteria,
                followType: 'SINGLE_TARGET_BASELINE_FOLLOW_GOAL',
                follow: goalActions.singleTargetBaselineFollowGoal,
            },
        },
    },
};

/**
 * A target's own Teaching -> Mastered (`kind="mastery"`) or Baseline -> Teaching (`kind="baseline"`) rule.
 * Edits stay in a draft until Save; "Use goal settings" is a separate switch and applies immediately.
 */
export const TargetCriteriaCard = ({ kind, target, isLibrary }) => {
    const card = CARDS[kind];
    const actions = card.actions[isLibrary ? 'library' : 'client'];
    const dispatch = useDispatch();
    const canEdit = RolePermission([PermissionList.GOAL_CREATE?.code]);
    const goalById = useSelector((state) => (isLibrary ? state.library.goalById : state.goals.goalById));
    const currentTarget = goalById?.targets?.find((i) => i?.id === target?.id);
    const stored = currentTarget?.[card.storedKey];
    const followLoader = FindLoad(actions.followType);
    const criteria = useCriteriaDraft({ fields: card.fields, actionType: actions.saveType });
    const following = !!criteria.draft?.followGoalSettings;
    const disabled = !canEdit || following;

    useEffect(() => {
        criteria.sync(stored ? { ...stored } : card.defaults);
    }, [target?.id, stored]);

    const toggleFollow = () => {
        const followGoalSettings = !following;
        dispatch(actions.follow(goalById?.id, target?.id, { followGoalSettings }));
        // Following the goal discards unsaved edits — the target's own rule no longer applies.
        if (followGoalSettings) criteria.reset();
        criteria.applySaved({ followGoalSettings });
    };

    return (
        <div className="mastery-box">
            <p className="mastery-title">{card.title}</p>
            <div className="mastery-info-box">
                <div className="mastery-status-row">
                    <div className="space-between-align-center" style={{ padding: 12 }}>
                        <div className="flex-align-center" style={{ gap: 4 }}>
                            <StatusCard
                                status={renderTargetStatuses?.find((j) => card.from === j?.value)?.label}
                                custom={renderTargetStatuses?.find((j) => card.from === j?.value)}
                            />
                            <img src={Images.arrowRight} alt="arrow" />
                            <StatusCard
                                status={renderTargetStatuses?.find((j) => card.to === j?.value)?.label}
                                custom={renderTargetStatuses?.find((j) => card.to === j?.value)}
                            />
                        </div>

                        <div className="flex-align-center" style={{ gap: 16 }}>
                            <FirstTrialSwitch
                                goal={goalById}
                                disabled={disabled}
                                checked={criteria.draft?.firstTrialOnly}
                                onToggle={() => criteria.update(toggleFirstTrial(criteria.draft))}
                            />
                            <div className="switcher-style" style={{ gap: 4 }}>
                                <ToolInfo text={card.autoInfo} />
                                <p style={{ width: 'auto', padding: 0, opacity: following ? 0.5 : 1 }}>{card.autoLabel}</p>
                                <CustomizedSwitch
                                    disabled={disabled}
                                    checked={criteria.draft?.[card.autoKey]}
                                    handleClick={() => criteria.update({ [card.autoKey]: !criteria.draft?.[card.autoKey] })}
                                />
                            </div>

                            <div className="switcher-style" style={{ gap: 4 }}>
                                <ToolInfo text={card.followInfo} />
                                <p style={{ width: 'auto', padding: 0 }}>{card.followLabel}</p>
                                <div style={{ width: 30, height: 21 }}>
                                    {followLoader?.length ? (
                                        <MinLoader margin={'0'} color={Colors.ThemeBlue} position={'relative'} />
                                    ) : (
                                        <Checkbox
                                            disabled={!canEdit}
                                            checked={following}
                                            onClick={toggleFollow}
                                            name={'followGoalSettings'}
                                            style={{ padding: 0, color: following ? '#347AF0' : '#D0D5DD' }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid #EBF2FD', padding: 12 }}>
                        <CriteriaRuleRow
                            goal={goalById}
                            subject={card.subject}
                            criteria={criteria.draft}
                            disabled={disabled}
                            onChange={criteria.update}
                        />
                    </div>
                    <CriteriaSaveBar
                        dirty={criteria.isDirty && !following}
                        saving={criteria.isSaving}
                        disabled={!isRuleValid(criteria.draft)}
                        onCancel={criteria.reset}
                        onSave={() => criteria.save((body) => dispatch(actions.save(goalById?.id, currentTarget?.id, body)))}
                    />
                </div>
            </div>
        </div>
    );
};
